import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import {
  Sparkles, Loader2, Calendar, FileSpreadsheet, Copy, Check, Plus, Trash2, RefreshCw, Wand2,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, AlertTriangle, Clapperboard, Target, LayoutGrid, List as ListIcon,
  Eye, Columns3, CalendarDays, Palette, TrendingUp, Crosshair, Users, MessageSquare, BarChart3,
  ThumbsUp, ThumbsDown, Layers, Repeat2, BookOpen, Paintbrush, Share2, CloudUpload, ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';
import { getFestivalsForMonth } from './contentCalendarFestivals';

// ─── Options ────────────────────────────────────────────────────────────────

const GOALS = [
  'Brand Awareness', 'Engagement & Community', 'Follower Growth', 'Lead Generation',
  'Sales & Offers', 'Product / Service Launch', 'Footfall to Store / Event',
];

const THEMES = [
  'Brand Awareness', 'Product Launch', 'Festive / Seasonal', 'Educational / How-To',
  'Engagement & Community', 'Lead Generation', 'Customer Testimonials', 'Behind the Scenes',
  'Offers & Discounts', 'User Generated Content', 'Influencer Collaboration', 'CSR / Social Cause',
  'Funny / Meme', 'Trendy / Trending',
];

const TONES = ['Friendly', 'Professional', 'Witty', 'Inspirational', 'Bold', 'Premium / Luxury', 'Emotional', 'Youthful'];

const LANGUAGES = ['English', 'Kannada', 'Kanglish', 'Hindi', 'Hinglish', 'Tamil', 'Telugu', 'Malayalam'];

const PLATFORMS = ['Instagram', 'Facebook', 'YouTube', 'LinkedIn', 'Twitter/X', 'Pinterest', 'WhatsApp'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type PostFormat = 'reel' | 'carousel' | 'static' | 'story';
const FORMATS: { key: PostFormat; label: string; plural: string }[] = [
  { key: 'reel', label: 'Reel', plural: 'Reels' },
  { key: 'carousel', label: 'Carousel', plural: 'Carousels' },
  { key: 'static', label: 'Static Post', plural: 'Static Posts' },
  { key: 'story', label: 'Story', plural: 'Stories' },
];
const FORMAT_LABEL: Record<PostFormat, string> = { reel: 'Reel', carousel: 'Carousel', static: 'Static Post', story: 'Story' };
const FORMAT_STYLE: Record<PostFormat, string> = {
  reel: 'bg-[#216974] text-white',
  carousel: 'bg-[#54c4b6] text-white',
  static: 'bg-[#e4c184] text-[#4a3a17]',
  story: 'bg-gray-700 text-white',
};

const PANEL: { key: string; label: string }[] = [
  { key: 'brand', label: 'Brand Manager' },
  { key: 'growth', label: 'Growth Analyst' },
  { key: 'audience', label: 'Target Audience' },
  { key: 'cultural', label: 'Cultural / Regional' },
  { key: 'production', label: 'Production Lead' },
  { key: 'compliance', label: 'Compliance' },
];

// ─── Types (mirror ContentCalendarService) ─────────────────────────────────

interface FestivalRow { id: string; date: string; name: string; enabled: boolean; verify?: boolean }

interface Strategy {
  objective: string;
  audienceInsight: string;
  brandVoice: string;
  pillars: { name: string; description: string; share: number }[];
  weeklyArcs: { week: number; theme: string; goal: string }[];
  series: { name: string; weekday: string; pillar: string; description: string }[];
  festivalPlan: { date: string; name: string; use: boolean; angle: string }[];
  kpis: string[];
  dos: string[];
  donts: string[];
  pageInsights: string[];
  styleGuide: string;
  recommendations: Recommendation[];
}

interface Recommendation {
  area: string; priority: 'high' | 'medium' | 'low'; finding: string; change: string; why: string; example: string;
  effort: 'quick win' | 'this month' | 'ongoing';
}

const PRIORITY_STYLE: Record<Recommendation['priority'], string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

const PRIORITY_BORDER: Record<Recommendation['priority'], string> = {
  high: 'border-red-200',
  medium: 'border-amber-200',
  low: 'border-gray-200',
};

const EFFORT_STYLE: Record<Recommendation['effort'], string> = {
  'quick win': 'bg-emerald-50 text-emerald-700',
  'this month': 'bg-blue-50 text-blue-700',
  'ongoing': 'bg-purple-50 text-purple-700',
};

/** The growth audit — grid of cards, 2 per row */
const GrowthAudit = ({ items, onRemove }: { items: Recommendation[]; onRemove?: (i: number) => void }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {items.map((r, i) => (
      <div
        key={i}
        className={`relative rounded-xl border-2 ${PRIORITY_BORDER[r.priority]} bg-white p-4 flex flex-col gap-2 shadow-sm hover:shadow-md transition-shadow`}
      >
        {/* Card header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${PRIORITY_STYLE[r.priority]}`}>
              {r.priority}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${EFFORT_STYLE[r.effort]}`}>
              {r.effort}
            </span>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(i)}
              title="Remove — it won't be applied to the posts"
              className="shrink-0 p-1 text-gray-300 hover:text-red-500 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {/* Area label */}
        <p className="text-xs font-bold text-[#216974] uppercase tracking-wide">{r.area}</p>

        {/* Main recommendation */}
        <p className="font-semibold text-gray-900 text-sm leading-snug">{r.change}</p>

        {/* Now / Why rows */}
        {(r.finding || r.why) && (
          <div className="space-y-1 mt-1">
            {r.finding && (
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-gray-500">Now: </span>{r.finding}
              </p>
            )}
            {r.why && (
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-gray-500">Why: </span>{r.why}
              </p>
            )}
          </div>
        )}

        {/* Example */}
        {r.example && (
          <p className="mt-auto pt-2 px-3 py-2 rounded-lg bg-gray-50 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed border border-gray-100">
            {r.example}
          </p>
        )}
      </div>
    ))}
  </div>
);

interface PageAnalysis {
  handle: string; bio?: string; daysSinceLastPost?: number | null; followers: number; engagementRate: number; averageLikes: number; averageComments: number;
  postsAnalysed: number; postsPerWeek: number; formatMix: { video: number; image: number; carousel: number };
  topPosts: { url: string; type: string; likes: number; comments: number; views: number; caption: string; postedAt: string }[];
  recentCaptions: string[];
}

interface Research {
  trends: string; pastTopics: string[];
  page?: PageAnalysis | null; pageError?: string; styleGuide?: string; styleError?: string;
}

interface Post {
  id: string; date: string; weekday: string; week: number; format: PostFormat; platforms: string[];
  pillar: string; series?: string; festival?: string; festivalAngle?: string; arc?: string;
  title: string; angle: string; hook: string; concept: string; visualDirection: string[]; caption: string;
  cta: string; hashtags: string[]; audio: string; postingTime: string; objective: string;
  review?: { scores: Record<string, number>; overall: number; notes: string[]; revised: boolean };
  error?: string;
}

interface ShootPlan {
  shootDays: { name: string; suggestedDate: string; location: string; slotIds: string[]; props: string[]; people: string[]; shotList: string[] }[];
  designQueue: string[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');
const isoDate = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const prettyDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`;
};
const uid = () => Math.random().toString(36).slice(2, 9);

function monthDates(year: number, month: number): string[] {
  const days = new Date(year, month, 0).getDate();
  return Array.from({ length: days }, (_, i) => isoDate(year, month, i + 1));
}
const weekdayOf = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
};

function defaultDates(year: number, month: number): Set<string> {
  return new Set(monthDates(year, month).filter((d) => weekdayOf(d) !== 0));
}
function defaultFestivals(year: number, month: number): FestivalRow[] {
  return getFestivalsForMonth(year, month).map((f) => ({ id: uid(), ...f, enabled: true }));
}

const linesToArr = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

function postToText(p: Post): string {
  return [
    `${prettyDate(p.date)} (${p.weekday}) — ${FORMAT_LABEL[p.format]} · ${p.platforms.join(', ')}`,
    `Pillar: ${p.pillar}${p.series ? ` · Series: ${p.series}` : ''}${p.festival ? ` · Festival: ${p.festival}` : ''}`,
    `Topic: ${p.title}`,
    `Hook: ${p.hook}`,
    `Concept: ${p.concept}`,
    `Visual direction:\n${p.visualDirection.map((v, i) => `${i + 1}. ${v}`).join('\n')}`,
    `Caption:\n${p.caption}`,
    `CTA: ${p.cta}`,
    `Hashtags: ${p.hashtags.join(' ')}`,
    `Audio: ${p.audio}`,
    `Post at: ${p.postingTime} · Objective: ${p.objective}`,
  ].join('\n');
}

// Same columns as the Excel "Calendar" sheet
const SHEET_COLUMNS: [string, (p: Post) => string][] = [
  ['Date', (p) => p.date], ['Day', (p) => p.weekday], ['Week', (p) => (p.week ? `Week ${p.week}` : '')],
  ['Format', (p) => FORMAT_LABEL[p.format]], ['Platforms', (p) => p.platforms.join(', ')], ['Pillar', (p) => p.pillar],
  ['Series', (p) => p.series || ''], ['Festival', (p) => p.festival || ''], ['Title / Topic', (p) => p.title], ['Hook', (p) => p.hook],
  ['Concept', (p) => p.concept], ['Visual Direction / Script', (p) => p.visualDirection.join('\n')], ['Caption', (p) => p.caption],
  ['CTA', (p) => p.cta], ['Hashtags', (p) => p.hashtags.join(' ')], ['Audio', (p) => p.audio], ['Posting Time', (p) => p.postingTime],
  ['Objective', (p) => p.objective], ['Panel Score', (p) => (p.review?.overall ? `${p.review.overall}/10` : '')],
  ['Status', (p) => (p.error ? 'Needs regenerate' : 'Draft')],
];

/** The strategy as label / value rows, like the Excel "Strategy" sheet. */
function strategyRows(s: Strategy): string[][] {
  const rows: string[][] = [
    ['Objective', s.objective], ['Audience insight', s.audienceInsight], ['Brand voice', s.brandVoice], [],
    ['Pillar', 'Share', 'What goes in it'], ...s.pillars.map((p) => [p.name, `${p.share}%`, p.description]), [],
    ['Week', 'Theme', 'Goal'], ...s.weeklyArcs.map((a) => [`Week ${a.week}`, a.theme, a.goal]), [],
  ];
  if (s.series.length) rows.push(['Recurring series', 'Day', 'Idea'], ...s.series.map((x) => [x.name, x.weekday, x.description]), []);
  if (s.festivalPlan.length) rows.push(['Festival', 'Date', 'Plan'], ...s.festivalPlan.map((f) => [f.name, f.date, f.use ? f.angle || 'Use' : `Skipped${f.angle ? ` — ${f.angle}` : ''}`]), []);
  rows.push(['KPIs', s.kpis.join('\n')], ['Do', s.dos.join('\n')], ["Don't", s.donts.join('\n')]);
  if (s.recommendations?.length) {
    rows.push([], ['Page growth audit', 'Priority / effort', 'Change', 'Now', 'Why', 'Example'],
      ...s.recommendations.map((r) => [r.area, `${r.priority} · ${r.effort}`, r.change, r.finding, r.why, r.example]));
  }
  return rows;
}

/** The calendar as an HTML table — pasting it into Google Sheets keeps one post per row, multi-line cells intact. */
function postsToSheetHtml(posts: Post[]): string {
  const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
  const head = SHEET_COLUMNS.map(([h]) => `<th><b>${esc(h)}</b></th>`).join('');
  const rows = posts.map((p) => `<tr>${SHEET_COLUMNS.map(([, f]) => `<td>${esc(f(p) || '')}</td>`).join('')}</tr>`).join('');
  return `<table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function postsToTsv(posts: Post[]): string {
  const cell = (v: string) => (/[\t\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [SHEET_COLUMNS.map(([h]) => h), ...posts.map((p) => SHEET_COLUMNS.map(([, f]) => f(p) || ''))]
    .map((r) => r.map(cell).join('\t')).join('\n');
}

/** Read an SSE response, buffering partial lines across chunks. */
async function readSSE(res: Response, onEvent: (data: any) => void) {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    for (const ev of events) {
      for (const line of ev.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        try { onEvent(JSON.parse(line.slice(6))); } catch { /* skip malformed */ }
      }
    }
  }
}

// ─── Small UI pieces ────────────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent text-sm';

const Label = ({ children, hint }: { children: ReactNode; hint?: string }) => (
  <label className="block text-sm font-bold text-gray-700 mb-2">
    {children} {hint && <span className="font-normal text-gray-400">{hint}</span>}
  </label>
);

const SectionTitle = ({ n, title, sub }: { n: number; title: string; sub?: string }) => (
  <div className="flex items-start gap-3 pt-2">
    <span className="w-7 h-7 shrink-0 rounded-full bg-[#216974] text-white text-sm font-bold flex items-center justify-center">{n}</span>
    <div>
      <h3 className="text-lg font-bold text-gray-900 leading-7">{title}</h3>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  </div>
);

const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
      active ? 'bg-[#216974] text-white border-[#216974]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#54c4b6]'
    }`}
  >
    {children}
  </button>
);

const scoreColor = (s: number) => (s >= 8 ? 'bg-emerald-100 text-emerald-700' : s >= 7 ? 'bg-[#54c4b6]/15 text-[#216974]' : 'bg-amber-100 text-amber-700');

interface BrandProfile { id: string; name: string; niche: string; audience: string; about: string; instagram: string }

const WEEK_COLORS = ['bg-[#216974]', 'bg-[#54c4b6]', 'bg-[#e4c184]', 'bg-indigo-400', 'bg-rose-400'];
const PILLAR_COLORS = ['border-l-[#216974]', 'border-l-[#54c4b6]', 'border-l-[#e4c184]', 'border-l-indigo-400', 'border-l-rose-400', 'border-l-violet-400', 'border-l-sky-400'];
const PILLAR_BG = ['bg-[#216974]', 'bg-[#54c4b6]', 'bg-[#e4c184]', 'bg-indigo-400', 'bg-rose-400', 'bg-violet-400', 'bg-sky-400'];

// ─── StrategyPanel (vertical left-nav + single-col right content) ─────────────

interface StrategyPanelProps {
  strategy: Strategy;
  research: Research;
  editStrategy: (patch: Partial<Strategy>) => void;
  inputCls: string;
  pillarSum: number;
  prettyDate: (iso: string) => string;
  linesToArr: (s: string) => string[];
}

type STab = 'overview' | 'pillars' | 'schedule' | 'style' | 'growth';

const StrategyPanel = ({ strategy, research, editStrategy, inputCls, pillarSum, prettyDate, linesToArr }: StrategyPanelProps) => {
  const [sTab, setSTab] = useState<STab>('overview');
  const hasGrowth = !!research.page || strategy.recommendations.length > 0;

  const tabs: { key: STab; label: string; sub: string; icon: ReactNode }[] = [
    { key: 'overview', label: 'Overview', sub: 'Objective, voice & KPIs', icon: <Eye size={14} /> },
    { key: 'pillars', label: 'Pillars', sub: 'Content pillars & share', icon: <Columns3 size={14} /> },
    { key: 'schedule', label: 'Schedule', sub: 'Weekly arcs & series', icon: <CalendarDays size={14} /> },
    { key: 'style', label: 'Style Guide', sub: 'Look & feel', icon: <Palette size={14} /> },
    ...(hasGrowth ? [{ key: 'growth' as STab, label: 'Growth', sub: 'Page audit & insights', icon: <TrendingUp size={14} /> }] : []),
  ];

  return (
    <div className="flex gap-0 min-h-[420px] border border-gray-200 rounded-xl overflow-hidden">

      {/* ── Left: vertical step nav ── */}
      <div className="w-48 shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col py-2">
        {tabs.map(({ key, label, sub, icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSTab(key)}
            className={`flex items-start gap-3 px-3 py-3 text-left transition-all relative ${
              sTab === key ? 'bg-white border-r-2 border-[#216974] shadow-sm' : 'hover:bg-gray-100'
            }`}
          >
            {/* Icon circle */}
            <span className={`mt-0.5 w-7 h-7 shrink-0 rounded-lg flex items-center justify-center transition-all ${
              sTab === key
                ? 'bg-[#216974] text-white shadow-md'
                : 'bg-gray-200/80 text-gray-500'
            }`}>
              {icon}
            </span>
            <div className="min-w-0">
              <p className={`text-sm font-semibold leading-tight ${sTab === key ? 'text-[#216974]' : 'text-gray-700'}`}>{label}</p>
              <p className="text-[10px] text-gray-400 leading-tight mt-0.5 line-clamp-2">{sub}</p>
            </div>
          </button>
        ))}
      </div>

      {/* ── Right: single-column stacked content ── */}
      <div className="flex-1 overflow-y-auto max-h-[420px] p-5 space-y-5 animate-in fade-in duration-200">

        {/* ── Overview: Objective · Audience · Voice · KPIs · Do · Don't stacked ── */}
        {sTab === 'overview' && (
          <div className="space-y-3">
            {([
              ['objective', 'Objective', '', 3, <Crosshair size={13} className="text-[#216974]" />],
              ['audienceInsight', 'Audience insight', '', 3, <Users size={13} className="text-[#216974]" />],
              ['brandVoice', 'Brand voice', '', 3, <MessageSquare size={13} className="text-[#216974]" />],
            ] as [string, string, string, number, ReactNode][]).map(([k, l, , rows, icon]) => (
              <div key={k} className="border border-gray-200 rounded-xl p-4 space-y-2 hover:border-[#216974]/30 transition-colors">
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#216974] uppercase tracking-wide">
                  {icon} {l}
                </label>
                <textarea
                  rows={rows}
                  value={strategy[k as keyof Strategy] as string}
                  onChange={(e) => editStrategy({ [k]: e.target.value } as Partial<Strategy>)}
                  className={`${inputCls} resize-none`}
                />
              </div>
            ))}
            {([
              ['kpis', 'KPIs', 'one per line', <BarChart3 size={13} className="text-[#216974]" />],
              ['dos', 'Do', 'one per line', <ThumbsUp size={13} className="text-emerald-600" />],
              ['donts', "Don't", 'one per line', <ThumbsDown size={13} className="text-red-400" />],
            ] as [string, string, string, ReactNode][]).map(([k, l, hint, icon]) => (
              <div key={k} className="border border-gray-200 rounded-xl p-4 space-y-2 hover:border-[#216974]/30 transition-colors">
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#216974] uppercase tracking-wide">
                  {icon} {l} <span className="font-normal text-gray-400 normal-case">({hint})</span>
                </label>
                <textarea
                  rows={4}
                  value={(strategy[k as keyof Strategy] as string[]).join('\n')}
                  onChange={(e) => editStrategy({ [k]: e.target.value.split('\n') } as Partial<Strategy>)}
                  onBlur={(e) => editStrategy({ [k]: linesToArr(e.target.value) } as Partial<Strategy>)}
                  className={`${inputCls} resize-none`}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Pillars ── */}
        {sTab === 'pillars' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-bold text-[#216974] uppercase tracking-wide"><Layers size={13} /> Content pillars</p>
              <span className={`text-xs font-semibold ${pillarSum === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {pillarSum}% {pillarSum !== 100 && '(will be scaled to 100%)'}
              </span>
            </div>
            {strategy.pillars.map((p, i) => (
              <div key={i} className={`border border-gray-200 border-l-4 ${PILLAR_COLORS[i % PILLAR_COLORS.length]} rounded-xl p-4 space-y-2 hover:shadow-sm transition-shadow`}>
                <div className="flex items-center gap-2">
                  <input value={p.name} onChange={(e) => editStrategy({ pillars: strategy.pillars.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                    className={`${inputCls} font-semibold`} placeholder="Pillar name" />
                  <div className="relative w-20 shrink-0">
                    <input type="number" min={0} max={100} value={p.share}
                      onChange={(e) => editStrategy({ pillars: strategy.pillars.map((x, j) => (j === i ? { ...x, share: parseInt(e.target.value) || 0 } : x)) })}
                      className={`${inputCls} pr-6`} />
                    <span className="absolute right-2 top-2 text-xs text-gray-400">%</span>
                  </div>
                  <button type="button" onClick={() => editStrategy({ pillars: strategy.pillars.filter((_, j) => j !== i) })} className="p-2 text-gray-400 hover:text-red-500 shrink-0">
                    <Trash2 size={15} />
                  </button>
                </div>
                <input value={p.description} onChange={(e) => editStrategy({ pillars: strategy.pillars.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })}
                  className={inputCls} placeholder="What goes in this pillar" />
                {/* Share bar */}
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${PILLAR_BG[i % PILLAR_BG.length]}`}
                    style={{ width: `${Math.min(p.share, 100)}%` }} />
                </div>
              </div>
            ))}
            {strategy.pillars.length < 6 && (
              <button type="button" onClick={() => editStrategy({ pillars: [...strategy.pillars, { name: '', description: '', share: 10 }] })}
                className="flex items-center gap-1.5 text-sm font-semibold text-[#216974] hover:underline">
                <Plus size={15} /> Add pillar
              </button>
            )}
          </div>
        )}

        {/* ── Schedule: weekly arcs + series + festival stacked ── */}
        {sTab === 'schedule' && (
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <p className="flex items-center gap-1.5 text-xs font-bold text-[#216974] uppercase tracking-wide"><CalendarDays size={13} /> Weekly storyline</p>
              {strategy.weeklyArcs.map((a, i) => (
                <div key={a.week} className="space-y-1.5 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                  <p className="flex items-center gap-2 text-xs font-bold text-gray-600">
                    <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white ${WEEK_COLORS[(a.week - 1) % WEEK_COLORS.length]}`}>{a.week}</span>
                    Week {a.week}
                  </p>
                  <div className="flex gap-2">
                    <input value={a.theme} onChange={(e) => editStrategy({ weeklyArcs: strategy.weeklyArcs.map((x, j) => (j === i ? { ...x, theme: e.target.value } : x)) })}
                      className={inputCls} placeholder="Theme" />
                    <input value={a.goal} onChange={(e) => editStrategy({ weeklyArcs: strategy.weeklyArcs.map((x, j) => (j === i ? { ...x, goal: e.target.value } : x)) })}
                      className={inputCls} placeholder="Goal" />
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <p className="flex items-center gap-1.5 text-xs font-bold text-[#216974] uppercase tracking-wide"><Repeat2 size={13} /> Recurring series</p>
              {strategy.series.map((s, i) => (
                <div key={i} className="space-y-1.5 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                  <div className="flex gap-2">
                    <input value={s.name} onChange={(e) => editStrategy({ series: strategy.series.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                      className={`${inputCls} font-semibold`} placeholder="Series name" />
                    <button type="button" onClick={() => editStrategy({ series: strategy.series.filter((_, j) => j !== i) })} className="p-2 text-gray-400 hover:text-red-500 shrink-0">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <select value={s.weekday} onChange={(e) => editStrategy({ series: strategy.series.map((x, j) => (j === i ? { ...x, weekday: e.target.value } : x)) })} className={inputCls}>
                      {WEEKDAYS.map((w) => <option key={w}>{w}</option>)}
                    </select>
                    <select value={s.pillar} onChange={(e) => editStrategy({ series: strategy.series.map((x, j) => (j === i ? { ...x, pillar: e.target.value } : x)) })} className={inputCls}>
                      <option value="">Any pillar</option>
                      {strategy.pillars.filter((p) => p.name).map((p) => <option key={p.name}>{p.name}</option>)}
                    </select>
                  </div>
                  <input value={s.description} onChange={(e) => editStrategy({ series: strategy.series.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })}
                    className={inputCls} placeholder="The repeatable idea" />
                </div>
              ))}
              {strategy.series.length < 3 && (
                <button type="button" onClick={() => editStrategy({ series: [...strategy.series, { name: '', weekday: 'Tuesday', pillar: '', description: '' }] })}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#216974] hover:underline">
                  <Plus size={15} /> Add series
                </button>
              )}
            </div>

            {strategy.festivalPlan.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-[#216974] uppercase tracking-wide">Festival plan</p>
                {strategy.festivalPlan.map((f, i) => (
                  <div key={`${f.date}-${f.name}`} className={`flex gap-2 items-center ${f.use ? '' : 'opacity-60'}`}>
                    <input type="checkbox" checked={f.use} onChange={(e) => editStrategy({ festivalPlan: strategy.festivalPlan.map((x, j) => (j === i ? { ...x, use: e.target.checked } : x)) })} className="w-4 h-4 accent-[#216974] shrink-0" />
                    <span className="text-sm font-semibold text-gray-700 w-40 shrink-0">{f.name} <span className="text-gray-400 font-normal text-xs">· {prettyDate(f.date)}</span></span>
                    <input value={f.angle} onChange={(e) => editStrategy({ festivalPlan: strategy.festivalPlan.map((x, j) => (j === i ? { ...x, angle: e.target.value } : x)) })}
                      className={inputCls} placeholder="How the brand shows up" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Style Guide ── */}
        {sTab === 'style' && (
          <div className="border border-gray-200 rounded-xl p-4 space-y-2">
            <label className="block text-xs font-bold text-[#216974] uppercase tracking-wide">
              <Paintbrush size={13} className="inline mr-1" /> Style guide
              {research.styleGuide
                ? <span className="font-normal text-gray-400 normal-case ml-1">(from the moodboard reel — edit freely)</span>
                : <span className="font-normal text-gray-400 normal-case ml-1">(optional)</span>}
            </label>
            <textarea
              rows={research.styleGuide ? 10 : 6}
              value={strategy.styleGuide}
              onChange={(e) => editStrategy({ styleGuide: e.target.value })}
              placeholder="e.g., Hooks are bold on-screen text questions, fast 1-2s cuts, warm golden-hour look, captions short with 2 emojis…"
              className={`${inputCls} resize-y`}
            />
          </div>
        )}

        {/* ── Growth: page stats + audit cards stacked one below another ── */}
        {sTab === 'growth' && (
          <div className="space-y-4">
            {research.page && (
              <>
                {/* Page stats */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <p className="font-bold text-gray-900 text-sm">@{research.page.handle}</p>
                    <p className="text-xs text-gray-400">last {research.page.postsAnalysed} posts</p>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[
                      ['Followers', research.page.followers.toLocaleString('en-IN')],
                      ['Engagement', `${research.page.engagementRate}%`],
                      ['Avg likes', research.page.averageLikes.toLocaleString('en-IN')],
                      ['Avg comments', research.page.averageComments.toLocaleString('en-IN')],
                      ['Posts/wk', research.page.postsPerWeek || '—'],
                    ].map(([l, v]) => (
                      <div key={l} className="rounded-lg bg-gray-50 py-2">
                        <p className="text-base font-bold text-[#216974]">{v}</p>
                        <p className="text-[10px] text-gray-500">{l}</p>
                      </div>
                    ))}
                  </div>
                  {(research.page.bio || research.page.daysSinceLastPost != null) && (
                    <p className="text-xs text-gray-600">
                      {research.page.bio ? <>Bio: <span className="italic">"{research.page.bio}"</span></> : 'No bio'}
                      {research.page.daysSinceLastPost != null && <> · last post {research.page.daysSinceLastPost} day(s) ago</>}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Format mix: {research.page.formatMix.video} reels · {research.page.formatMix.carousel} carousels · {research.page.formatMix.image} images</p>
                  {research.page.topPosts.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase mb-1">Top performers</p>
                      <ul className="space-y-1 text-xs">
                        {research.page.topPosts.map((t) => (
                          <li key={t.url} className="flex gap-2">
                            <span className="shrink-0 font-semibold text-gray-500 w-24">{t.likes.toLocaleString('en-IN')} likes</span>
                            <a href={t.url} target="_blank" rel="noreferrer" className="text-gray-600 hover:text-[#216974] truncate">{t.caption || '(no caption)'}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Page insights <span className="font-normal normal-case">(one per line)</span></label>
                    <textarea rows={3} value={strategy.pageInsights.join('\n')}
                      onChange={(e) => editStrategy({ pageInsights: e.target.value.split('\n') })}
                      onBlur={(e) => editStrategy({ pageInsights: linesToArr(e.target.value) })}
                      className={`${inputCls} resize-none`} />
                  </div>
                </div>
              </>
            )}

            {/* Growth audit — one card per recommendation stacked vertically */}
            {strategy.recommendations.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-[#216974] uppercase tracking-wide">
                  <TrendingUp size={13} className="inline mr-1" /> Page growth audit <span className="font-normal text-gray-400 normal-case">(remove any you don't want applied to posts)</span>
                </p>
                {strategy.recommendations.map((r, i) => (
                  <div key={i} className={`rounded-xl border-2 ${PRIORITY_BORDER[r.priority]} bg-white p-4 space-y-2`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${EFFORT_STYLE[r.effort]}`}>{r.effort}</span>
                        <span className="text-[10px] font-bold text-[#216974] uppercase">{r.area}</span>
                      </div>
                      <button type="button" onClick={() => editStrategy({ recommendations: strategy.recommendations.filter((_, j) => j !== i) })}
                        title="Remove — won't be applied to posts" className="p-1 text-gray-300 hover:text-red-500 shrink-0">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{r.change}</p>
                    {r.finding && <p className="text-xs text-gray-600"><span className="font-semibold text-gray-500">Now: </span>{r.finding}</p>}
                    {r.why && <p className="text-xs text-gray-600"><span className="font-semibold text-gray-500">Why: </span>{r.why}</p>}
                    {r.example && <p className="text-xs text-gray-700 bg-gray-50 rounded-lg px-3 py-2 whitespace-pre-wrap border border-gray-100">{r.example}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};


// ─── LocalStorage persistence helper ─────────────────────────────────────────

const CALENDAR_STORAGE_KEY = 'agy_ai_calendar_v1';

function loadStorage(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(CALENDAR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveStorage(patch: Record<string, unknown>) {
  try {
    const existing = loadStorage();
    localStorage.setItem(CALENDAR_STORAGE_KEY, JSON.stringify({ ...existing, ...patch }));
  } catch { /* quota exceeded — silently ignore */ }
}


// ─── Component ──────────────────────────────────────────────────────────────

const AIContentIdeas = () => {
  const today = new Date();

  // ── Load persisted brief inputs from localStorage ──
  const _s = loadStorage();

  // Brief
  const [brand, setBrand] = useState<string>(() => (_s.brand as string) || '');
  const [about, setAbout] = useState<string>(() => (_s.about as string) || '');
  const [audience, setAudience] = useState<string>(() => (_s.audience as string) || '');
  const [goal, setGoal] = useState<string>(() => (_s.goal as string) || GOALS[0]);
  const [themes, setThemes] = useState<string[]>(() => (_s.themes as string[]) || ['Brand Awareness', 'Educational / How-To', 'Engagement & Community']);
  const [tones, setTones] = useState<string[]>(() => (_s.tones as string[]) || ['Friendly']);
  const [language, setLanguage] = useState<string>(() => (_s.language as string) || 'English');
  const [platforms, setPlatforms] = useState<string[]>(() => (_s.platforms as string[]) || ['Instagram']);
  const [month, setMonth] = useState<number>(() => (_s.month as number) || today.getMonth() + 1);
  const [year, setYear] = useState<number>(() => (_s.year as number) || today.getFullYear());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(() => {
    if (_s.selectedDates) return new Set(_s.selectedDates as string[]);
    return defaultDates(today.getFullYear(), today.getMonth() + 1);
  });
  const [formatCounts, setFormatCounts] = useState<Record<PostFormat, number>>(
    () => (_s.formatCounts as Record<PostFormat, number>) || { reel: 12, carousel: 6, static: 8, story: 0 }
  );
  const [festivals, setFestivals] = useState<FestivalRow[]>(
    () => (_s.festivals as FestivalRow[]) || defaultFestivals(today.getFullYear(), today.getMonth() + 1)
  );
  const [keyDates, setKeyDates] = useState<string>(() => (_s.keyDates as string) || '');
  const [references, setReferences] = useState<string>(() => (_s.references as string) || '');
  const [instagram, setInstagram] = useState<string>(() => (_s.instagram as string) || '');
  const [moodboardUrl, setMoodboardUrl] = useState<string>(() => (_s.moodboardUrl as string) || '');

  // Brand profiles from Nammajai — picking one pre-fills the brief, which stays editable
  const [brandProfiles, setBrandProfiles] = useState<BrandProfile[]>([]);
  const [brandProfileId, setBrandProfileId] = useState('');
  const [brandProfilesError, setBrandProfilesError] = useState<string | null>(null);

  // Pipeline — restored from localStorage so a page refresh doesn't lose generated data
  const [strategy, setStrategy] = useState<Strategy | null>(() => (_s.strategy as Strategy) || null);
  const [research, setResearch] = useState<Research>(() => (_s.research as Research) || { trends: '', pastTopics: [] });
  const [buildingStrategy, setBuildingStrategy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ message: string; percent: number }>({ message: '', percent: 0 });
  const [posts, setPosts] = useState<Post[]>(() => (_s.posts as Post[]) || []);
  const [shootPlan, setShootPlan] = useState<ShootPlan>(() => (_s.shootPlan as ShootPlan) || { shootDays: [], designQueue: [] });
  const [warnings, setWarnings] = useState<string[]>(() => (_s.warnings as string[]) || []);
  const [error, setError] = useState<string | null>(null);

  // Results UI
  const [tab, setTab] = useState<'calendar' | 'list' | 'shoot' | 'strategy'>('calendar');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [drawerPost, setDrawerPost] = useState<Post | null>(null);
  const [regenId, setRegenId] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [copied, setCopied] = useState<string | null>(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; msg: string; url?: string } | null>(null);

  const _hadSavedData = useRef(!!(_s.brand || (_s.posts as unknown[])?.length));
  const [showRestoreBanner, setShowRestoreBanner] = useState(_hadSavedData.current);
  const strategyRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const skipMonthReset = useRef(false);

  // ── Persist brief inputs whenever they change ──
  useEffect(() => {
    saveStorage({ brand, about, audience, goal, themes, tones, language, platforms, month, year,
      selectedDates: Array.from(selectedDates), formatCounts, festivals, keyDates, references, instagram, moodboardUrl });
  }, [brand, about, audience, goal, themes, tones, language, platforms, month, year,
      selectedDates, formatCounts, festivals, keyDates, references, instagram, moodboardUrl]);

  // ── Persist generated output whenever it changes ──
  useEffect(() => {
    saveStorage({ strategy, research, posts, shootPlan, warnings });
  }, [strategy, research, posts, shootPlan, warnings]);

    useEffect(() => {
    fetch('/api/nammajai/brands', { credentials: 'include' })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (data.success) setBrandProfiles(data.brands || []);
        else setBrandProfilesError(data.error || 'Could not load brands.');
      })
      .catch(() => setBrandProfilesError('Could not load brands.'));
  }, []);

  const pickBrandProfile = (id: string) => {
    setBrandProfileId(id);
    const b = brandProfiles.find((x) => x.id === id);
    if (!b) return;
    setBrand(b.niche ? `${b.name} — ${b.niche}` : b.name);
    setAudience(b.audience);
    setAbout(b.about);
    if (b.instagram) setInstagram(b.instagram);
  };

  // Changing month/year resets dates and festivals (but not when restoring from history)
  useEffect(() => {
    if (skipMonthReset.current) { skipMonthReset.current = false; return; }
    setSelectedDates(defaultDates(year, month));
    setFestivals(defaultFestivals(year, month));
  }, [year, month]);

  const allDates = useMemo(() => monthDates(year, month), [year, month]);
  const enabledFestivals = festivals.filter((f) => f.enabled && f.name.trim() && f.date.startsWith(`${year}-${pad(month)}`));
  const postingDays = useMemo(() => {
    const s = new Set(selectedDates);
    enabledFestivals.forEach((f) => s.add(f.date));
    return s.size;
  }, [selectedDates, enabledFestivals]);
  const totalPieces = FORMATS.reduce((a, f) => a + (formatCounts[f.key] || 0), 0);
  const festivalByDate = useMemo(() => {
    const m = new Map<string, string[]>();
    enabledFestivals.forEach((f) => m.set(f.date, [...(m.get(f.date) || []), f.name]));
    return m;
  }, [enabledFestivals]);

  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const brief = () => ({
    brand: brand.trim(), about, audience, goal, themes, tones, language, platforms, month, year,
    dates: [...selectedDates].sort(), formatCounts,
    festivals: enabledFestivals.map((f) => ({ date: f.date, name: f.name.trim() })),
    keyDates, references, instagram: instagram.trim(), moodboardUrl: moodboardUrl.trim(),
  });

  const validate = (): string | null => {
    if (!brand.trim()) return 'Please enter your brand / niche';
    if (platforms.length === 0) return 'Select at least one platform';
    if (totalPieces === 0) return 'Enter how many reels, carousels, posts or stories you need';
    if (postingDays === 0) return 'Select at least one posting date';
    if (totalPieces > postingDays * 2) return `${totalPieces} pieces won't fit in ${postingDays} posting days (max 2 per day). Add dates or reduce the counts.`;
    if (totalPieces > 70) return 'Maximum 70 pieces per calendar';
    return null;
  };

  // ── Date picker actions ──
  const toggleDate = (d: string) => setSelectedDates((prev) => {
    const n = new Set(prev);
    n.has(d) ? n.delete(d) : n.add(d);
    return n;
  });
  const toggleWeekday = (wd: number) => setSelectedDates((prev) => {
    const days = allDates.filter((d) => weekdayOf(d) === wd);
    const allOn = days.every((d) => prev.has(d));
    const n = new Set(prev);
    days.forEach((d) => (allOn ? n.delete(d) : n.add(d)));
    return n;
  });
  const presetDates = (kind: 'all' | 'noSun' | 'weekdays' | 'alternate' | 'none') => {
    const pick: Record<typeof kind, (d: string, i: number) => boolean> = {
      all: () => true,
      noSun: (d) => weekdayOf(d) !== 0,
      weekdays: (d) => weekdayOf(d) !== 0 && weekdayOf(d) !== 6,
      alternate: (_d, i) => i % 2 === 0,
      none: () => false,
    };
    setSelectedDates(new Set(allDates.filter(pick[kind])));
  };

  // ── Clear all persisted data ──
  const clearAll = () => {
    if (!confirm('Clear all saved inputs and generated calendar? This cannot be undone.')) return;
    try { localStorage.removeItem(CALENDAR_STORAGE_KEY); } catch {}
    setBrand(''); setAbout(''); setAudience(''); setGoal(GOALS[0]);
    setThemes(['Brand Awareness', 'Educational / How-To', 'Engagement & Community']);
    setTones(['Friendly']); setLanguage('English'); setPlatforms(['Instagram']);
    setMonth(today.getMonth() + 1); setYear(today.getFullYear());
    setSelectedDates(defaultDates(today.getFullYear(), today.getMonth() + 1));
    setFormatCounts({ reel: 12, carousel: 6, static: 8, story: 0 });
    setFestivals(defaultFestivals(today.getFullYear(), today.getMonth() + 1));
    setKeyDates(''); setReferences(''); setInstagram(''); setMoodboardUrl('');
    setStrategy(null); setResearch({ trends: '', pastTopics: [] });
    setPosts([]); setShootPlan({ shootDays: [], designQueue: [] }); setWarnings([]);
    setCurrentStep(1); setError(null); setNotice(null);
  };

    // ── Step 1: strategy ──
  const handleBuildStrategy = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setBuildingStrategy(true);
    try {
      const res = await fetch('/api/writing/content-calendar/strategy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brief: brief() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to build strategy');
      setStrategy(data.strategy);
      setResearch(data.research || { trends: '', pastTopics: [] });
      setPosts([]);
      setTimeout(() => strategyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBuildingStrategy(false);
    }
  };

  // ── Step 2: generate ──
  const handleGenerate = async () => {
    if (!strategy) return;
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setGenerating(true);
    setPosts([]);
    setWarnings([]);
    setProgress({ message: 'Starting…', percent: 2 });
    try {
      const res = await fetch('/api/writing/content-calendar/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brief: brief(), strategy, research }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to generate calendar');
      }
      let finished = false;
      await readSSE(res, (ev) => {
        if (ev.type === 'progress') setProgress({ message: ev.message, percent: ev.percent });
        else if (ev.type === 'complete') {
          finished = true;
          setPosts(ev.posts || []);
          setShootPlan(ev.shootPlan || { shootDays: [], designQueue: [] });
          setWarnings(ev.warnings || []);
          setTab('calendar');
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        } else if (ev.type === 'error') {
          finished = true;
          setError(ev.error || 'Generation failed');
        }
      });
      if (!finished) throw new Error('Connection closed before the calendar finished. Please try again.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // ── Per-post regenerate ──
  const handleRegenerate = async (p: Post, keepTopic: boolean) => {
    if (!strategy) return;
    setRegenId(p.id);
    try {
      const res = await fetch('/api/writing/content-calendar/regenerate-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brief: brief(), strategy, research, slot: p,
          avoidTitles: posts.filter((x) => x.id !== p.id && x.title).map((x) => x.title).concat(keepTopic ? [] : [p.title]),
          instruction: instructions[p.id] || '',
          keepTopic: keepTopic ? p.title : '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Regenerate failed');
      setPosts((prev) => prev.map((x) => (x.id === p.id ? data.post : x)));
      const [, m, d] = p.date.split('-').map(Number);
      setWarnings((prev) => prev.filter((w) => !w.startsWith(`${MONTHS[m - 1]} ${d},`)));
    } catch (err: any) {
      alert(err.message);
    } finally {
      setRegenId(null);
    }
  };

  const handleCopy = (p: Post) => {
    navigator.clipboard.writeText(postToText(p));
    setCopied(p.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownloadExcel = async () => {
    if (posts.length === 0) return;
    setExcelLoading(true);
    try {
      const res = await fetch('/api/writing/content-calendar-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ posts, strategy, shootPlan, month: MONTHS[month - 1], year }),
      });
      if (!res.ok) { alert('Excel export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-calendar-${brand.trim().replace(/\s+/g, '-') || 'brand'}-${MONTHS[month - 1]}-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Excel export failed');
    } finally {
      setExcelLoading(false);
    }
  };

  // ── Google Sheets: the server creates a filled sheet (company Google account, shared by link) and we open it ──
  const handleGoogleSheets = async () => {
    if (posts.length === 0) return;
    // Open the tab straight from the click (browsers block tabs opened after a network wait), fill it in when ready
    const win = window.open('', '_blank');
    win?.document.write('<p style="font-family:sans-serif;padding:24px;color:#216974">Creating your Google Sheet…</p>');
    setSheetsLoading(true);
    setNotice(null);
    try {
      const res = await fetch('/api/writing/content-calendar/google-sheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: `${brand.split(' — ')[0].trim() || 'Brand'} — ${MONTHS[month - 1]} ${year} Content Calendar`,
          tabs: [
            { title: 'Calendar', header: true, rows: [SHEET_COLUMNS.map(([h]) => h), ...posts.map((p) => SHEET_COLUMNS.map(([, f]) => f(p) || ''))], wideColumns: [8, 9, 10, 11, 12] },
            ...(strategy ? [{ title: 'Strategy', rows: strategyRows(strategy), wideColumns: [1, 2, 3, 4, 5] }] : []),
          ],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 503) {
        // Google not connected on this server yet — fall back to copy & paste
        win?.close();
        await handlePasteToSheets();
        return;
      }
      if (!res.ok || !data.url) throw new Error(data.error || 'Could not create the Google Sheet');
      if (win) win.location.href = data.url;
      else window.open(data.url, '_blank');
      setNotice({ ok: true, msg: `Google Sheet created with ${posts.length} posts.`, url: data.url });
    } catch (err: any) {
      win?.close();
      setNotice({ ok: false, msg: err.message });
    } finally {
      setSheetsLoading(false);
    }
  };

  // ── Copy the calendar as a table and open a blank sheet; the user pastes ──
  const handlePasteToSheets = async () => {
    if (posts.length === 0) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([postsToSheetHtml(posts)], { type: 'text/html' }),
        'text/plain': new Blob([postsToTsv(posts)], { type: 'text/plain' }),
      })]);
    } catch {
      // Clipboard blocked (old browser / permissions) — fall back to a CSV file to import
      return handleCsvDownload();
    }
    const win = window.open('https://sheets.new', '_blank');
    setNotice({
      ok: true,
      msg: `Copied ${posts.length} posts. ${win ? 'In the new Google Sheet' : 'Open a new Google Sheet (sheets.new), then'} click cell A1 and press ${/Mac/i.test(navigator.platform) ? '⌘' : 'Ctrl'}+V.`,
    });
  };

  const handleCsvDownload = async () => {
    setSheetsLoading(true);
    try {
      const res = await fetch('/api/writing/content-calendar-gsheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ posts, strategy, shootPlan, month: MONTHS[month - 1], year, brand }),
      });
      if (!res.ok) { alert('Google Sheets export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-calendar-${brand.trim().replace(/\s+/g, '-') || 'brand'}-${MONTHS[month - 1]}-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setNotice({ ok: true, msg: 'Downloaded a CSV. In Google Sheets: File → Import → Upload, and pick this file.' });
    } catch {
      alert('Google Sheets export failed');
    } finally {
      setSheetsLoading(false);
    }
  };

  // ── Sync to Nammajai ──
  const handleSyncNammajai = async () => {
    if (posts.length === 0) return;
    setSyncLoading(true);
    setNotice(null);
    try {
      const res = await fetch('/api/writing/content-calendar/sync-nammajai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brandId: brandProfileId || null, brand, month, year, posts, strategy, shootPlan }),
      });
      const data = await res.json().catch(() => ({ success: false, error: 'Unknown error' }));
      if (data.success) {
        setNotice({ ok: true, msg: `${MONTHS[month - 1]} ${year} calendar sent to Nammajai.` });
      } else {
        setNotice({ ok: false, msg: data.error || 'Sync failed' });
      }
    } catch (err: any) {
      setNotice({ ok: false, msg: err.message || 'Network error' });
    } finally {
      setSyncLoading(false);
    }
  };

  // ── Restore from history ──
  const restoreSnapshot = (snapStr: string) => {
    try {
      const snap = JSON.parse(snapStr);
      const b = snap.brief || {};
      if (b.month !== month || b.year !== year) skipMonthReset.current = true;
      setBrand(b.brand || ''); setAbout(b.about || ''); setAudience(b.audience || '');
      setGoal(b.goal || GOALS[0]); setThemes(b.themes || []); setTones(b.tones || []);
      setLanguage(b.language || 'English'); setPlatforms(b.platforms || ['Instagram']);
      setMonth(b.month || month); setYear(b.year || year);
      setSelectedDates(new Set(b.dates || []));
      setFormatCounts({ reel: 0, carousel: 0, static: 0, story: 0, ...(b.formatCounts || {}) });
      setFestivals((b.festivals || []).map((f: any) => ({ id: uid(), date: f.date, name: f.name, enabled: true })));
      setKeyDates(b.keyDates || ''); setReferences(b.references || '');
      setInstagram(b.instagram || ''); setMoodboardUrl(b.moodboardUrl || '');
      setStrategy(snap.strategy ? { pageInsights: [], styleGuide: '', recommendations: [], ...snap.strategy } : null);
      setResearch(snap.research || { trends: '', pastTopics: [] });
      setPosts(snap.posts || []);
      setShootPlan(snap.shootPlan || { shootDays: [], designQueue: [] });
      setWarnings(snap.warnings || []);
      setTab('calendar');
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } catch {
      alert('Could not restore this calendar');
    }
  };

  // ── Strategy editing helpers ──
  const editStrategy = (patch: Partial<Strategy>) => setStrategy((s) => (s ? { ...s, ...patch } : s));
  const pillarSum = strategy?.pillars.reduce((a, p) => a + (Number(p.share) || 0), 0) || 0;

  const postById = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);
  const avgScore = useMemo(() => {
    const s = posts.filter((p) => p.review?.overall).map((p) => p.review!.overall);
    return s.length ? Math.round((s.reduce((a, b) => a + b, 0) / s.length) * 10) / 10 : 0;
  }, [posts]);

  const openPost = (id: string) => {
    const p = posts.find((x) => x.id === id);
    if (p) setDrawerPost(p);
  };

  const yearOptions = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];
  const firstWeekday = weekdayOf(allDates[0]);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#216974] to-[#54c4b6] rounded-2xl shadow-lg">
          <Calendar size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Meet Anamika, your AI content planner</h1>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto font-medium">
          Planned like a senior social media team: strategy first, then unique ideas, production-ready scripts and captions,
          checked by a six-person review panel, with a shoot plan.
        </p>
      </div>

      {/* ── BRIEF ── */}
      <Card className="overflow-hidden border-0 shadow-2xl">
        {/* Autosave restored banner */}
        <AnimatePresence>
          {showRestoreBanner && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="flex items-center justify-between px-6 pt-4 pb-0">
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <Check size={12} className="shrink-0" />
                {posts.length > 0 ? 'Calendar restored from autosave — no need to regenerate!' : 'Inputs restored from autosave'}
              </span>
              <button type="button" onClick={() => setShowRestoreBanner(false)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="p-6 md:p-8 space-y-6">
          {/* ── Stepper bar ── */}
          <div className="relative flex items-center justify-between mb-2">
            {[
              { n: 1, label: 'Brand' },
              { n: 2, label: 'Goals' },
              { n: 3, label: 'Platforms' },
              { n: 4, label: 'Dates' },
              { n: 5, label: 'Festivals' },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex items-center flex-1 last:flex-none">
                <button
                  type="button"
                  onClick={() => setCurrentStep(s.n)}
                  className="flex flex-col items-center gap-1 group relative z-10"
                >
                  <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      currentStep === s.n
                        ? 'bg-[#216974] text-white ring-4 ring-[#216974]/20'
                        : s.n < currentStep
                        ? 'bg-[#54c4b6] text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {s.n < currentStep ? <Check size={16} /> : s.n}
                  </span>
                  <span
                    className={`text-xs font-semibold whitespace-nowrap ${
                      currentStep === s.n ? 'text-[#216974]' : s.n < currentStep ? 'text-[#54c4b6]' : 'text-gray-400'
                    }`}
                  >
                    {s.label}
                  </span>
                </button>
                {i < arr.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mt-[-18px] rounded ${s.n < currentStep ? 'bg-[#54c4b6]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* ── Step 1: Brand & audience ── */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <SectionTitle n={1} title="Brand & audience" />
              <div>
                <Label hint="(optional — pre-fills the fields below, which you can still edit)">Select brand</Label>
                <select value={brandProfileId} onChange={(e) => pickBrandProfile(e.target.value)} className={inputCls} disabled={!brandProfiles.length}>
                  <option value="">{brandProfiles.length ? '— Enter details manually —' : brandProfilesError || 'Loading brands…'}</option>
                  {brandProfiles.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Brand / Niche *</Label>
                  <Input placeholder="e.g., Sripada Studios — wedding photography, Bengaluru" value={brand} onChange={(e) => setBrand(e.target.value)} />
                </div>
                <div>
                  <Label hint="(optional)">Target audience</Label>
                  <Input placeholder="e.g., Couples 24–32 planning weddings in Karnataka" value={audience} onChange={(e) => setAudience(e.target.value)} />
                </div>
              </div>
              <div>
                <Label hint="(optional — the more context, the better)">About the brand / campaign brief</Label>
                <textarea
                  rows={3}
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="What you sell, what makes you different, price range, key messages, what's worked before…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label hint="(optional — public page)">Brand's Instagram page</Label>
                  <Input placeholder="@yourbrand or instagram.com/yourbrand" value={instagram} onChange={(e) => setInstagram(e.target.value)} />
                  <p className="text-xs text-gray-500 mt-1">Reads recent posts and top performers so the plan builds on what works and doesn't repeat.</p>
                </div>
                <div>
                  <Label hint="(optional)">Moodboard reel link</Label>
                  <Input placeholder="https://www.instagram.com/reel/…" value={moodboardUrl} onChange={(e) => setMoodboardUrl(e.target.value)} />
                  <p className="text-xs text-gray-500 mt-1">A reel whose style you love — its hook, pacing, text and caption style become the standard for every post.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Goal, themes & voice ── */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <SectionTitle n={2} title="Goal, themes & voice" sub="Pick as many themes as you want — the strategist turns them into content pillars." />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary goal this month</Label>
                  <select value={goal} onChange={(e) => setGoal(e.target.value)} className={inputCls}>
                    {GOALS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Language</Label>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inputCls}>
                    {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Themes / objectives</Label>
                <div className="flex flex-wrap gap-2">
                  {THEMES.map((t) => <Chip key={t} active={themes.includes(t)} onClick={() => toggle(themes, setThemes, t)}>{t}</Chip>)}
                </div>
                {themes.includes('Trendy / Trending') && (
                  <p className="text-xs text-gray-500 mt-2">Current trends are looked up on the web when the strategy is built.</p>
                )}
              </div>
              <div>
                <Label>Tone</Label>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => <Chip key={t} active={tones.includes(t)} onClick={() => toggle(tones, setTones, t)}>{t}</Chip>)}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Platforms & deliverables ── */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <SectionTitle n={3} title="Platforms & deliverables" />
              <div>
                <Label>Platforms</Label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => <Chip key={p} active={platforms.includes(p)} onClick={() => toggle(platforms, setPlatforms, p)}>{p}</Chip>)}
                </div>
              </div>
              <div>
                <Label>How many of each?</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {FORMATS.map((f) => (
                    <div key={f.key} className="p-3 rounded-xl border border-gray-200 bg-gray-50">
                      <p className="text-xs font-bold text-gray-600 mb-1">{f.plural}</p>
                      <input
                        type="number"
                        min={0}
                        max={70}
                        value={formatCounts[f.key]}
                        onChange={(e) => setFormatCounts((c) => ({ ...c, [f.key]: Math.max(0, Math.min(70, parseInt(e.target.value) || 0)) }))}
                        className={inputCls}
                      />
                    </div>
                  ))}
                </div>
                <p className={`text-xs mt-2 ${totalPieces > postingDays * 2 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  {totalPieces} pieces across {postingDays} posting days
                  {totalPieces > postingDays * 2 ? ' — too many (max 2 per day)' : totalPieces < postingDays ? ` — ${postingDays - totalPieces} selected days will stay free` : totalPieces > postingDays ? ` — some days get 2 pieces` : ''}
                </p>
              </div>
            </div>
          )}

          {/* ── Step 4: Calendar dates ── */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <SectionTitle n={4} title="Calendar dates" sub="Click days to turn them on/off, or click a weekday heading to toggle that weekday." />
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Month</Label>
                    <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className={inputCls}>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Year</Label>
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className={inputCls}>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 md:pt-7">
                  {([['all', 'All days'], ['noSun', 'Skip Sundays'], ['weekdays', 'Mon–Fri'], ['alternate', 'Alternate days'], ['none', 'Clear']] as const).map(([k, l]) => (
                    <button key={k} type="button" onClick={() => presetDates(k)} className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-300 text-gray-600 hover:border-[#54c4b6]">
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1.5 max-w-xl">
                {DAY_SHORT.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleWeekday(i)} className="text-xs font-bold text-gray-500 py-1 hover:text-[#216974]">{d}</button>
                ))}
                {Array.from({ length: firstWeekday }).map((_, i) => <div key={`b${i}`} />)}
                {allDates.map((d) => {
                  const on = selectedDates.has(d);
                  const fest = festivalByDate.get(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDate(d)}
                      title={fest ? fest.join(', ') : undefined}
                      className={`relative h-10 rounded-lg text-sm font-semibold border transition-all ${
                        on ? 'bg-[#216974] text-white border-[#216974]' : fest ? 'bg-[#e4c184]/30 text-gray-700 border-[#e4c184]' : 'bg-white text-gray-400 border-gray-200 hover:border-[#54c4b6]'
                      }`}
                    >
                      {Number(d.slice(8))}
                      {fest && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#e4c184]" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500">Festival days you keep enabled below always get a post, even if the day is switched off here.</p>
            </div>
          )}

          {/* ── Step 5: Festivals & key dates ── */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <SectionTitle n={5} title="Festivals & key dates" sub="Edit dates, switch off festivals that don't fit, or add your own (anniversary, sale, launch)." />
              <div className="space-y-2">
                {festivals.length === 0 && <p className="text-sm text-gray-400">No festivals listed for this month — add your own below.</p>}
                {festivals.map((f) => (
                  <div key={f.id} className={`grid grid-cols-[auto_140px_1fr_auto] gap-2 items-center ${f.enabled ? '' : 'opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={f.enabled}
                      onChange={(e) => setFestivals((list) => list.map((x) => (x.id === f.id ? { ...x, enabled: e.target.checked } : x)))}
                      className="w-4 h-4 accent-[#216974]"
                    />
                    <input
                      type="date"
                      value={f.date}
                      min={allDates[0]}
                      max={allDates[allDates.length - 1]}
                      onChange={(e) => setFestivals((list) => list.map((x) => (x.id === f.id ? { ...x, date: e.target.value, verify: false } : x)))}
                      className={inputCls}
                    />
                    <div className="flex items-center gap-2">
                      <input
                        value={f.name}
                        onChange={(e) => setFestivals((list) => list.map((x) => (x.id === f.id ? { ...x, name: e.target.value } : x)))}
                        className={inputCls}
                        placeholder="Festival / occasion"
                      />
                      {f.verify && (
                        <span title="Lunar date — please check it against your local calendar" className="shrink-0 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">
                          verify date
                        </span>
                      )}
                    </div>
                    <button type="button" onClick={() => setFestivals((list) => list.filter((x) => x.id !== f.id))} className="p-2 text-gray-400 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFestivals((list) => [...list, { id: uid(), date: allDates[0], name: '', enabled: true }])}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#216974] hover:underline"
                >
                  <Plus size={15} /> Add date
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label hint="(optional)">Launches, offers & events</Label>
                  <textarea rows={2} value={keyDates} onChange={(e) => setKeyDates(e.target.value)} className={`${inputCls} resize-none`}
                    placeholder="e.g., New studio opening on 15th; 20% off pre-wedding shoots till 30th" />
                </div>
                <div>
                  <Label hint="(optional)">Reference / competitor accounts</Label>
                  <textarea rows={2} value={references} onChange={(e) => setReferences(e.target.value)} className={`${inputCls} resize-none`}
                    placeholder="e.g., @brandA for reel style, @brandB is our main competitor" />
                </div>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-semibold flex items-center gap-2">
                    <AlertTriangle size={18} /> {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                onClick={handleBuildStrategy}
                isLoading={buildingStrategy}
                disabled={buildingStrategy || generating}
                className="w-full h-12 text-lg rounded-xl"
                leftIcon={buildingStrategy ? undefined : <Target size={20} />}
              >
                {buildingStrategy
                  ? (instagram.trim() || moodboardUrl.trim()
                    ? `Analysing ${[instagram.trim() && 'your Instagram page', moodboardUrl.trim() && 'the moodboard reel'].filter(Boolean).join(' and ')}, then planning… (up to 2 min)`
                    : 'Strategist is planning your month…')
                  : strategy ? 'Rebuild strategy from this brief' : 'Step 1 · Build content strategy'}
              </Button>
            </div>
          )}

          {/* ── Navigation buttons ── */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep((s) => s - 1)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
            ) : <div />}
            <button
              type="button"
              onClick={clearAll}
              title="Clear all saved inputs and generated calendar"
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={12} /> Clear saved
            </button>
            {currentStep < 5 && (
              <button
                type="button"
                onClick={() => setCurrentStep((s) => s + 1)}
                className="flex items-center gap-1.5 px-6 py-2.5 text-sm font-bold text-white rounded-xl transition-colors"
                style={{ background: 'linear-gradient(135deg, #216974 0%, #54c4b6 100%)' }}
              >
                Next <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* ── STRATEGY (editable — tabbed) ── */}
      {strategy && (
        <div ref={strategyRef}>
          <Card className="overflow-hidden border-0 shadow-xl">
            <div className="p-6 md:p-8 space-y-4">
              {/* Header */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Content strategy — {MONTHS[month - 1]} {year}</h2>
                <p className="text-sm text-gray-500 mt-0.5">Review and edit anything. Every post is written from this plan.</p>
              </div>

              {/* Alerts */}
              {themes.includes('Trendy / Trending') && !research.trends && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Live trend search didn't return results — "Trendy" ideas use proven trending formats. Add any trend or audio you want in the brief.
                </p>
              )}
              {research.pageError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Instagram page couldn't be analysed ({research.pageError}). The plan was made without it.
                </p>
              )}
              {research.styleError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Moodboard reel couldn't be analysed ({research.styleError}). Describe the style in the Style & KPIs tab.
                </p>
              )}

              {/* Strategy tabs */}
              <StrategyPanel
                strategy={strategy}
                research={research}
                editStrategy={editStrategy}
                inputCls={inputCls}
                pillarSum={pillarSum}
                prettyDate={prettyDate}
                linesToArr={linesToArr}
              />

              {/* Generate button */}
              {generating ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#216974]">
                    <Loader2 size={16} className="animate-spin" /> {progress.message}
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-gradient-to-r from-[#216974] to-[#54c4b6]" animate={{ width: `${progress.percent}%` }} transition={{ duration: 0.5 }} />
                  </div>
                  <p className="text-xs text-gray-400">Usually 1–3 minutes. Every post is written, then checked by the review panel.</p>
                </div>
              ) : (
                <Button onClick={handleGenerate} disabled={buildingStrategy} className="w-full h-12 text-lg rounded-xl" leftIcon={<Sparkles size={20} />}>
                  Step 2 · Generate {totalPieces}-piece calendar
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ── RESULTS ── */}
      <AnimatePresence>
        {posts.length > 0 && (
          <motion.div ref={resultsRef} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}>
            <Card className="border-0 shadow-xl overflow-hidden">
              {/* Header */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-gray-100">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{MONTHS[month - 1]} {year} Content Calendar</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {brand} · {FORMATS.map((f) => {
                      const n = posts.filter((p) => p.format === f.key).length;
                      return n ? `${n} ${n === 1 ? f.label : f.plural}` : '';
                    }).filter(Boolean).join(' · ')}
                    {avgScore > 0 && <> · Panel avg <b>{avgScore}/10</b></>}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleDownloadExcel} disabled={excelLoading} variant="secondary" className="rounded-lg"
                    leftIcon={excelLoading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}>
                    {excelLoading ? 'Generating…' : 'Excel'}
                  </Button>
                  <Button onClick={handleGoogleSheets} disabled={sheetsLoading} variant="secondary" className="rounded-lg"
                    leftIcon={sheetsLoading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}>
                    {sheetsLoading ? 'Exporting…' : 'Google Sheets'}
                  </Button>
                  <Button onClick={handleSyncNammajai} disabled={syncLoading} variant="secondary" className="rounded-lg"
                    leftIcon={syncLoading ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />}>
                    {syncLoading ? 'Sending…' : 'Send to Nammajai'}
                  </Button>
                </div>
              </div>

              {warnings.length > 0 && (
                <div className="mx-4 mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 space-y-1">
                  {warnings.map((w, i) => <p key={i} className="flex gap-2"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {w}</p>)}
                </div>
              )}

              {/* Sync result toast */}
              <AnimatePresence>
                {notice && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className={`mx-4 mt-3 p-3 rounded-lg flex items-center justify-between text-xs font-medium ${
                      notice.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {notice.ok ? <Check size={14} /> : <AlertTriangle size={14} />}
                      {notice.msg}
                      {notice.url && (
                        <a href={notice.url} target="_blank" rel="noreferrer" className="font-bold underline">Open Google Sheet</a>
                      )}
                    </span>
                    <button type="button" onClick={() => setNotice(null)} className="p-1 hover:opacity-60">✕</button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tab bar */}
              <div className="flex gap-1 px-4 pt-3 border-b border-gray-100 overflow-x-auto">
                {([['calendar', 'Calendar', LayoutGrid], ['list', 'Posts', ListIcon], ['shoot', 'Shoot plan', Clapperboard], ['strategy', 'Strategy', Target]] as const).map(([k, l, Icon]) => (
                  <button key={k} type="button" onClick={() => setTab(k)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-[#216974] text-[#216974]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <Icon size={14} /> {l}
                  </button>
                ))}
              </div>

              {/* Tab content — fixed height, internal scroll */}
              <div className="p-4 max-h-[65vh] overflow-y-auto">

                {/* ── Calendar grid — compact cells ── */}
                {tab === 'calendar' && (
                  <div className="overflow-x-auto">
                    <div className="grid grid-cols-7 gap-1 min-w-[560px]">
                      {DAY_SHORT.map((d) => <div key={d} className="text-[10px] font-bold text-gray-400 text-center py-1">{d}</div>)}
                      {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e${i}`} />)}
                      {allDates.map((d) => {
                        const dayPosts = posts.filter((p) => p.date === d);
                        const fest = festivalByDate.get(d);
                        return (
                          <div key={d} className={`min-h-[56px] rounded-lg border p-1 ${fest ? 'border-[#e4c184] bg-[#e4c184]/10' : 'border-gray-200 bg-white'}`}>
                            <div className="text-[10px] font-bold text-gray-500 mb-0.5">{Number(d.slice(8))}</div>
                            {fest && <div className="text-[9px] font-semibold text-[#8a6a2a] leading-tight mb-0.5 line-clamp-1">{fest.join(', ')}</div>}
                            <div className="space-y-0.5">
                              {dayPosts.map((p) => (
                                <button key={p.id} type="button" onClick={() => openPost(p.id)}
                                  className={`w-full text-left rounded px-1 py-0.5 text-[9px] leading-tight font-semibold ${p.error ? 'bg-red-100 text-red-700' : FORMAT_STYLE[p.format]}`}>
                                  <span className="line-clamp-2">{FORMAT_LABEL[p.format][0]} · {p.title || '…'}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Post list — compact single-line rows, click opens drawer ── */}
                {tab === 'list' && (
                  <div className="space-y-1.5">
                    {posts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => openPost(p.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left hover:shadow-sm transition-shadow ${
                          p.error ? 'border-red-200 bg-red-50/40' : p.festival ? 'border-[#e4c184] bg-[#e4c184]/5' : 'border-gray-200 bg-white hover:border-[#54c4b6]/40'
                        }`}
                      >
                        <div className="w-14 shrink-0 text-center">
                          <p className="text-xs font-bold text-gray-800 leading-tight">{prettyDate(p.date).replace(/, \d{4}$/, '')}</p>
                          <p className="text-[10px] text-gray-400">{p.weekday.slice(0, 3)}</p>
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${FORMAT_STYLE[p.format]}`}>{FORMAT_LABEL[p.format]}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.title || 'Not generated'}</p>
                          <p className="text-[10px] text-gray-400 truncate">{p.pillar}{p.series ? ` · ${p.series}` : ''}{p.festival ? ` · ${p.festival}` : ''}</p>
                        </div>
                        {p.review?.overall ? (
                          <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-lg ${scoreColor(p.review.overall)}`}>{p.review.overall}</span>
                        ) : null}
                        <ChevronRight size={14} className="text-gray-300 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}

                {/* ── Shoot plan ── */}
                {tab === 'shoot' && (
                  <div className="space-y-4">
                    {shootPlan.shootDays.length === 0 && <p className="text-sm text-gray-500">No shoot days needed — everything is in the design queue.</p>}
                    {shootPlan.shootDays.map((d, i) => (
                      <div key={i} className="rounded-xl border border-gray-200 p-4 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-bold text-gray-900">{d.name}</p>
                          <span className="text-xs font-semibold text-[#216974]">{d.suggestedDate ? `Shoot by ${prettyDate(d.suggestedDate)}` : ''}{d.location ? ` · ${d.location}` : ''}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {d.slotIds.map((id) => {
                            const p = postById.get(id);
                            return p ? (
                              <button key={id} type="button" onClick={() => openPost(id)} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${FORMAT_STYLE[p.format]}`}>
                                {prettyDate(p.date).replace(/, \d{4}$/, '')} · {p.title}
                              </button>
                            ) : null;
                          })}
                        </div>
                        <div className="grid md:grid-cols-3 gap-3 text-sm">
                          <div><p className="text-xs font-bold text-gray-500 uppercase">Props</p><p className="text-gray-700">{d.props.join(', ') || '—'}</p></div>
                          <div><p className="text-xs font-bold text-gray-500 uppercase">People</p><p className="text-gray-700">{d.people.join(', ') || '—'}</p></div>
                          <div><p className="text-xs font-bold text-gray-500 uppercase">Key shots</p><ul className="text-gray-700 list-disc pl-4">{d.shotList.map((s, j) => <li key={j}>{s}</li>)}</ul></div>
                        </div>
                      </div>
                    ))}
                    {shootPlan.designQueue.length > 0 && (
                      <div className="rounded-xl border border-dashed border-gray-300 p-4">
                        <p className="font-bold text-gray-900 mb-2">Design queue <span className="font-normal text-gray-500 text-sm">(no shoot needed)</span></p>
                        <div className="flex flex-wrap gap-1.5">
                          {shootPlan.designQueue.map((id) => {
                            const p = postById.get(id);
                            return p ? (
                              <button key={id} type="button" onClick={() => openPost(id)} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${FORMAT_STYLE[p.format]}`}>
                                {prettyDate(p.date).replace(/, \d{4}$/, '')} · {p.title}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Strategy summary (uses StrategyPanel) ── */}
                {tab === 'strategy' && strategy && (
                  <StrategyPanel
                    strategy={strategy}
                    research={research}
                    editStrategy={editStrategy}
                    inputCls={inputCls}
                    pillarSum={pillarSum}
                    prettyDate={prettyDate}
                    linesToArr={linesToArr}
                  />
                )}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── POST DETAIL DRAWER ── */}
      <AnimatePresence>
        {drawerPost && (() => {
          const p = drawerPost;
          return (
            <>
              {/* Backdrop */}
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 z-40 backdrop-blur-[2px]"
                onClick={() => setDrawerPost(null)}
              />
              {/* Slide-in drawer */}
              <motion.div
                key="drawer"
                initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
              >
                {/* Drawer header */}
                <div className="flex items-start gap-3 p-5 border-b border-gray-100 shrink-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${FORMAT_STYLE[p.format]}`}>{FORMAT_LABEL[p.format]}</span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">{p.pillar}</span>
                      {p.series && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#54c4b6]/15 text-[#216974]">{p.series}</span>}
                      {p.festival && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#e4c184]/30 text-[#6b5320]">{p.festival}</span>}
                    </div>
                    <p className="font-bold text-gray-900">{p.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{prettyDate(p.date)} · {p.weekday}{p.postingTime ? ` · ${p.postingTime}` : ''} · {p.platforms.join(', ')}</p>
                  </div>
                  <button type="button" onClick={() => setDrawerPost(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0">
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Drawer body — scrollable */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
                  {p.error ? (
                    <p className="text-red-600">{p.error}</p>
                  ) : (
                    <>
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Hook</p><p className="font-semibold text-gray-900">{p.hook}</p></div>
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Concept</p><p className="text-gray-700">{p.concept}</p></div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase mb-1">
                          {p.format === 'reel' ? 'Scene beats' : p.format === 'carousel' ? 'Slides' : p.format === 'story' ? 'Frames' : 'Design notes'}
                        </p>
                        <ol className="list-decimal pl-5 text-gray-700 space-y-0.5">{p.visualDirection.map((v, i) => <li key={i}>{v}</li>)}</ol>
                      </div>
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Caption</p><p className="text-gray-700 whitespace-pre-wrap">{p.caption}</p></div>
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">CTA</p><p className="text-gray-700">{p.cta}</p></div>
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Hashtags</p><p className="text-[#216974]">{p.hashtags.join(' ')}</p></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Audio</p><p className="text-gray-700">{p.audio || '—'}</p></div>
                        <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">Objective</p><p className="text-gray-700">{p.objective || '—'}</p></div>
                      </div>
                      {p.review && (
                        <div className="rounded-xl bg-gray-50 p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-gray-800">Review panel</p>
                            <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${scoreColor(p.review.overall)}`}>{p.review.overall}/10</span>
                          </div>
                          {p.review.revised && <p className="text-xs font-semibold text-[#216974]">Scores are for the first draft — the panel rewrote it to fix these notes.</p>}
                          <div className="space-y-1.5">
                            {PANEL.map((m) => {
                              const s = p.review!.scores[m.key] || 0;
                              return (
                                <div key={m.key} className="flex items-center gap-2 text-xs">
                                  <span className="w-32 text-gray-600">{m.label}</span>
                                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div className={`h-full ${s >= 7 ? 'bg-[#54c4b6]' : 'bg-amber-400'}`} style={{ width: `${s * 10}%` }} />
                                  </div>
                                  <span className="w-5 text-right font-semibold text-gray-700">{s || '–'}</span>
                                </div>
                              );
                            })}
                          </div>
                          {p.review.notes.length > 0 && (
                            <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">{p.review.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Drawer footer — actions */}
                <div className="shrink-0 p-4 border-t border-gray-100 space-y-2 bg-white">
                  <input
                    value={instructions[p.id] || ''}
                    onChange={(e) => setInstructions((m) => ({ ...m, [p.id]: e.target.value }))}
                    placeholder="What should change? e.g. make it funnier, add a price mention"
                    className={inputCls}
                  />
                  <div className="flex gap-2">
                    {p.title && (
                      <Button size="sm" variant="secondary" disabled={regenId !== null}
                        onClick={() => handleRegenerate(p, true)}
                        leftIcon={regenId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}>
                        Rewrite
                      </Button>
                    )}
                    <Button size="sm" variant="secondary" disabled={regenId !== null}
                      onClick={() => handleRegenerate(p, false)}
                      leftIcon={regenId === p.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}>
                      New idea
                    </Button>
                    {!p.error && (
                      <Button size="sm" variant="secondary" onClick={() => handleCopy(p)}
                        leftIcon={copied === p.id ? <Check size={14} className="text-[#54c4b6]" /> : <Copy size={14} />}>
                        {copied === p.id ? 'Copied' : 'Copy'}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          );
        })()}
      </AnimatePresence>


      <HistorySection
        toolId="ai-ideas"
        onUseHistory={(entry) => {
          if (entry.snapshot) restoreSnapshot(entry.snapshot);
          else alert('This is an older calendar that cannot be reopened. Generate a new one.');
        }}
      />
    </div>
  );
};

export default AIContentIdeas;
